import { Box, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import { ExtractedRecord, type DocumentTransformation } from "@/types";
import type { SchemaFieldDefinition } from "@/constants/schema-fields/schemaFields";
import type { CriticalFieldAdvisoryContext } from "../../hooks/useCriticalFieldAdvisory";
import type { MappingValidationError } from "../../utils/fieldMappingValidation";
import { TargetFieldSelect, type UnifiedTarget } from "./TargetFieldSelect";
import { MappingBlockingAlert } from "./MappingBlockingAlert";
import { useMappingBlockers, rankSourcesByFieldMatch, type BlockingItem } from "./mappingBlockers";
import { useMappingOptions, useClaimedKeys, suggestedFieldForSource, currentTargetsForHeader } from "./mappingOptions";

interface HorizontalMappingGridProps {
  transformation: DocumentTransformation;
  /** Records to preview (#6755) — the transformation no longer carries them. */
  records?: ExtractedRecord[];
  /** Source columns in spreadsheet order. */
  detectedHeaders: string[];
  /** Current mappings, keyed schema → header → field names. */
  editedMappingsBySchema: Record<string, Record<string, string[]>>;
  /** Field definitions per schema — the union feeds the dropdown. */
  fieldsBySchema: Record<string, SchemaFieldDefinition[]>;
  /** All target schema keys on this transformation. */
  allSchemaKeys: string[];
  /** Commit a header's complete cross-schema mapping. */
  onChange: (header: string, targets: { schema: string; field: string }[]) => void;
  /** Per-schema validation errors from the shared validator — the blocking
   *  alert's duplicate detection reads `duplicate_target` from here so it can't
   *  drift from what gates Approve. */
  validationErrors?: Record<string, MappingValidationError[]>;
  /** Document context for conditional required-field gating (denial reason,
   *  family-unit subscriber id, etc.). Passed through to the canonical
   *  critical-slot resolver so the blocking alert matches real gating. */
  advisoryContext?: CriticalFieldAdvisoryContext;
}

const PREVIEW_ROW_COUNT = 10;
const COL_W = 172; // per-column width (Excel-ish)
const GUTTER_W = 44; // row-number gutter
const HEAD_H = 30; // column-header row height
const CELL_H = 26; // data cell height

/**
 * Source-mirrored remap surface for spreadsheets (#6101). Renders each detected
 * source column left-to-right in spreadsheet order — a "Maps to" dropdown above
 * each column (the shared `TargetFieldSelect`) over a gridlined preview of the
 * sheet — with the shared upload-blocking alert on top. Mirrors the horizontal
 * shape of the Excel the reviewer knows, instead of per-schema tabs.
 */
// `transformation` stays on the props interface (call sites pass it; the PDF
// sibling genuinely needs it) but is deliberately NOT destructured here — its
// only use was the stale memo dependency #6856 removed.
export const HorizontalMappingGrid = ({
  records,
  detectedHeaders,
  editedMappingsBySchema,
  fieldsBySchema,
  allSchemaKeys,
  onChange,
  advisoryContext = {},
  validationErrors,
}: HorizontalMappingGridProps) => {
  const { options, optionByKey, criticalByKey } = useMappingOptions(fieldsBySchema, allSchemaKeys);
  const claimedKeys = useClaimedKeys(editedMappingsBySchema, allSchemaKeys);

  // Up to N source rows as an aligned matrix — one object per source record,
  // keyed by column header — so the preview renders like the actual sheet.
  //
  // Depends on `records`, NOT `transformation` (#6856). Records arrive on an
  // async fetch fired when the dialog opens, so the first render always sees
  // [] — a memo keyed on the transformation computed zero rows once and never
  // recomputed, leaving the preview permanently empty. (Pre-#6755 the records
  // rode ON the transformation object, which made the wrong dependency
  // accidentally correct.)
  const rows = useMemo(() => {
    return (records ?? []).slice(0, PREVIEW_ROW_COUNT).map((rec) => {
      const row: Record<string, string> = {};
      for (const f of rec.extracted_fields ?? []) {
        const key = f.source?.key;
        if (!key) continue;
        row[key] = f.extracted_value === null || f.extracted_value === undefined ? "" : String(f.extracted_value);
      }
      return row;
    });
  }, [records]);

  // Column order: detected headers (source order) when present, else the union
  // of keys seen across the preview rows.
  const columns = useMemo(() => {
    if (detectedHeaders.length > 0) return detectedHeaders;
    const set = new Set<string>();
    rows.forEach((r) => Object.keys(r).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [detectedHeaders, rows]);

  const colIndexByHeader = useMemo(() => {
    const m = new Map<string, number>();
    columns.forEach((h, i) => {
      if (!m.has(h)) m.set(h, i);
    });
    return m;
  }, [columns]);

  // ── Blocking issues (shared) + jump-to-column locate ────────────────────────
  const blockers = useMappingBlockers({
    allSchemaKeys,
    editedMappingsBySchema,
    validationErrors,
    advisoryContext,
    optionByKey,
  });
  const colRefs = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const [flashCol, setFlashCol] = useState<number | null>(null);
  const scrollToCol = (i: number) => {
    if (i < 0) return;
    colRefs.current.get(i)?.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    setFlashCol(i);
  };
  useEffect(() => {
    if (flashCol === null) return;
    const t = setTimeout(() => setFlashCol(null), 1600);
    return () => clearTimeout(t);
  }, [flashCol]);

  // Map a blocking item to the source columns to cycle through: the offending
  // columns for a duplicate, or the best token-matched columns for a missing field.
  const resolveTargets = (item: BlockingItem): number[] => {
    // Duplicate and unknown-field both carry the exact offending source headers,
    // so jump to those columns precisely. Only missing-required has no known
    // source and falls back to best-guess token matching. (#6425)
    if (item.kind === "duplicate" || item.kind === "unknown-field") {
      return (item.sourceHeaders ?? []).map((h) => colIndexByHeader.get(h) ?? -1).filter((i) => i >= 0);
    }
    const opt = optionByKey.get(item.key);
    return rankSourcesByFieldMatch(opt?.label ?? item.label, opt?.field ?? "", columns);
  };

  const currentTargetsFor = (header: string): UnifiedTarget[] =>
    currentTargetsForHeader(header, allSchemaKeys, editedMappingsBySchema, optionByKey);

  if (columns.length === 0) {
    return (
      <Box sx={{ py: 6, textAlign: "center", color: "text.secondary" }}>
        <Typography variant="body2">No detected columns in this document.</Typography>
      </Box>
    );
  }

  const lastRow = rows.length - 1;
  const lastCol = columns.length - 1;

  return (
    <>
      <MappingBlockingAlert
        items={blockers}
        resolveTargets={resolveTargets}
        onLocate={scrollToCol}
        sourceUnit="column"
      />

      <Box sx={{ overflowX: "auto", pb: 1 }}>
        <Box sx={{ width: "fit-content" }}>
          {/* ── Mapping band — the "Maps to" dropdowns, ABOVE the sheet with
              whitespace between. A thin underline (teal = mapped, amber = needs
              a decision) anchors each dropdown to its column below. ── */}
          <Box sx={{ display: "flex", mb: 2, alignItems: "flex-end" }}>
            <Box sx={{ flexShrink: 0, width: GUTTER_W }} />
            {columns.map((header, ci) => {
              const value = currentTargetsFor(header);
              const isMapped = value.length > 0;
              const suggestedKey = suggestedFieldForSource(header, options);
              return (
                <Box
                  key={`map-${header}-${ci}`}
                  ref={(el: HTMLDivElement | null) => {
                    colRefs.current.set(ci, el);
                  }}
                  sx={{
                    flexShrink: 0,
                    width: COL_W,
                    px: 0.5,
                    pb: 0.5,
                    borderBottom: 2,
                    borderColor: isMapped ? "primary.main" : "warning.main",
                    borderRadius: flashCol === ci ? 1 : 0,
                    boxShadow: (theme) => (flashCol === ci ? `0 0 0 2px ${theme.palette.error.main}` : "none"),
                    transition: "box-shadow 200ms",
                    scrollMarginInline: "48px",
                  }}
                >
                  <TargetFieldSelect
                    options={options}
                    criticalByKey={criticalByKey}
                    value={value}
                    onChange={(targets) => onChange(header, targets)}
                    suggestedKey={suggestedKey}
                    claimedKeys={claimedKeys}
                  />
                </Box>
              );
            })}
          </Box>

          {/* ── Spreadsheet — gridlined header + data rows, read-only, like the
            source file. ── */}
          <Box sx={{ display: "flex", border: 1, borderColor: "divider", borderRadius: 1, overflow: "hidden" }}>
            {/* Row-number gutter */}
            <Box sx={{ flexShrink: 0, width: GUTTER_W, borderRight: 1, borderColor: "divider" }}>
              <Box sx={{ height: HEAD_H, borderBottom: 1, borderColor: "divider", bgcolor: "action.hover" }} />
              {rows.map((_r, i) => (
                <Box
                  key={i}
                  sx={{
                    height: CELL_H,
                    borderBottom: i < lastRow ? 1 : 0,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: "tabular-nums" }}>
                    {i + 1}
                  </Typography>
                </Box>
              ))}
            </Box>

            {/* One column per source header — header cell + data cells. */}
            {columns.map((header, ci) => (
              <Box
                key={`${header}-${ci}`}
                sx={{ flexShrink: 0, width: COL_W, borderRight: ci < lastCol ? 1 : 0, borderColor: "divider" }}
              >
                <Box
                  sx={{
                    height: HEAD_H,
                    px: 0.75,
                    display: "flex",
                    alignItems: "center",
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "action.hover",
                  }}
                >
                  <Tooltip title={header} arrow placement="top">
                    <Typography variant="caption" fontWeight={700} noWrap>
                      {header}
                    </Typography>
                  </Tooltip>
                </Box>

                {rows.map((r, ri) => (
                  <Box
                    key={ri}
                    sx={{
                      height: CELL_H,
                      px: 0.75,
                      display: "flex",
                      alignItems: "center",
                      borderBottom: ri < lastRow ? 1 : 0,
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.secondary",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {r[header] ?? ""}
                    </Typography>
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
};
